import React, { useState, useEffect, useRef, useContext, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'markdown-to-jsx'
import parse from 'html-react-parser'

import BEMHelper from '@ynap/bem-helper'
import Totals from '@ynap/totals'
import Skeleton from '@ynap/skeleton'
import { Button } from '@ynap/button'
import { LineItem, LineItemSkeleton } from '@ynap/line-item'
import Overlay from '@ynap/overlay'
import Notification from '@ynap/notification'
import { LiteLayout } from '@ynap/product-details-page'
import { SingleBadge } from '@ynap/badge'

import OrderPaymentSummary from '../../OrderHistory/OrderPaymentSummary/OrderPaymentSummary'
import ReturnMessage from './ReturnMessage'
import { NavContext } from '../../MyAccountNav/MyAccountNavWrapper/MyAccountNavWrapper'
import OrderDetailsStatus from '../OrderDetailsStatus'
import ReturnsAndExchanges from './ReturnsAndExchanges'

const bem = new BEMHelper('OrderInformation')

const propTypes = {
    messages: PropTypes.shape({
        orderDetails: PropTypes.shape({
            title: PropTypes.func.isRequired,
            orderInfo: PropTypes.func.isRequired,
            trackOrder: PropTypes.func.isRequired,
            dateShipped: PropTypes.func.isRequired,
            returnInfo: PropTypes.func,
            pageError: PropTypes.func.isRequired,
            retryButton: PropTypes.func.isRequired,
            timeDelivered: PropTypes.func.isRequired,
        }),
        considered: PropTypes.shape({
            badge: PropTypes.func.isRequired,
        }),
        price: PropTypes.shape({
            discountPercentage: PropTypes.number,
        }),
    }),
    orderDate: PropTypes.string,
    orderDateRaw: PropTypes.string,
    orderItemTotal: PropTypes.string,
    shippingAddress: PropTypes.object,
    shippingMethod: PropTypes.array,
    trackingUrl: PropTypes.string,
    isReturnable: PropTypes.string,
    isPartialReturn: PropTypes.bool,
    shortDateShipped: PropTypes.string,
    dateShipped: PropTypes.string,
    dateShippedRaw: PropTypes.string,
    externalOrderId: PropTypes.string,
    displayId: PropTypes.string,
    paymentMethod: PropTypes.array,
    locale: PropTypes.object.isRequired,
    totals: PropTypes.object,
    userTitle: PropTypes.string,
    userLastName: PropTypes.string,
    returnsCountry: PropTypes.string,
    isLegacyOrder: PropTypes.string,
    isExchangable: PropTypes.string,
    timeDelivered: PropTypes.string,
    timeDeliveredRaw: PropTypes.string,
    urlPath: PropTypes.string,
    isOrderCancelled: PropTypes.bool,
    activeOrderStatus: PropTypes.string,
    isReturnRefused: PropTypes.bool,
    isOrderDetailsFetched: PropTypes.bool,
    bookReturnEnabledInCountry: PropTypes.bool,
}

function usePrevious(value) {
    const ref = useRef()
    useEffect(() => {
        ref.current = value
    }, [value])
    return ref.current
}

const splitWithFinalSaleItems = (shipmentItems) =>
    shipmentItems?.reduce(
        (acc, curr) => {
            if (curr?.finalSale) {
                return {
                    ...acc,
                    finalSaleItems: [...acc.finalSaleItems, curr],
                }
            }

            return {
                ...acc,
                regularItems: [...acc.regularItems, curr],
            }
        },
        {
            finalSaleItems: [],
            regularItems: [],
        }
    )

const OrderInformation = (props) => {
    const {
        messages,
        orderDate,
        orderItemTotal,
        shippingAddress,
        shippingMethod,
        trackingUrl,
        returnsCountry,
        isReturnable,
        shortDateShipped,
        dateShipped,
        timeDelivered,
        externalOrderId,
        displayId,
        guestUser,
        paymentMethod,
        locale,
        totals,
        userTitle,
        userLastName,
        isLegacyOrder,
        isExchangable,
        urlPath,
        exchanges,
        returns,
        config,
        returnTrackingUrl,
        shipmentsWithItemDetails,
        isEip,
    } = props

    const [showOverlay, setShowOverlay] = useState(false)
    const prevShowOverlay = usePrevious(showOverlay)
    const { setNavigation } = useContext(NavContext) || {}

    const hasExchanges = exchanges?.length > 0

    useEffect(() => {
        setNavigation?.({
            preTitle: messages?.pages?.myorders?.title?.(),
            title: messages?.pages?.orderdetails?.title?.(),
        })
    }, [])

    const toggleOverlay = (partNumber) => {
        setShowOverlay(partNumber || !prevShowOverlay)
    }

    const head = config?.head
    const { fullPageHeader, enableOmnistock, enableFinalSaleOrderItemsSplit } = head?.flags || {}
    const returnWindowConfig = head?.returnWindow
    const deliveryTrackingEnabled = head?.deliveryTrackingService?.enabled
    const orderDelayMessaging = head?.orderDelayMessaging
    const returnsAllowedInCountry = returnsCountry
        ? returnsCountry.toLowerCase() === locale.country.toLowerCase()
        : false
    const showOrderDetailsStatus = enableOmnistock
        ? shipmentsWithItemDetails.length == 1 &&
          (deliveryTrackingEnabled || head?.deliveryTrackingAnalytics) // TODO deliveryTrackingAnalytics might not be needed anymore?
        : deliveryTrackingEnabled || head?.deliveryTrackingAnalytics
    const showReturnsAndExchanges = !deliveryTrackingEnabled || (deliveryTrackingEnabled && hasExchanges)
    const showDelayMessaging =
        orderDelayMessaging?.enabled &&
        orderDelayMessaging.locales.includes(locale.id) &&
        !orderDelayMessaging?.exclude?.includes('orderDetails')
    const returnWindow = returnWindowConfig?.countries?.includes?.(shippingAddress?.country)
        ? returnWindowConfig?.specialWindow
        : returnWindowConfig?.default

    const splitShipment = shipmentsWithItemDetails?.length > 1

    const delayMessage =
        orderDelayMessaging?.message?.[locale.language] ||
        messages.orderDetails.delayMessage?.({ country: locale.country })

    const returnFeeInfo = head?.returnFeeInfo?.[locale.country.toLowerCase()]
    const displayReturnFeeMessage = !isEip && !!returnFeeInfo

    const DelayComponent = (props) => <ReactMarkdown {...props}>{delayMessage}</ReactMarkdown>

    const renderItem = (item, index) => {
        return item ? (
            <Fragment key={`shipmentItems-${index}`}>
                <LineItem
                    isLegacyOrder={isLegacyOrder}
                    {...item}
                    messages={messages}
                    onClick={() => toggleOverlay(`${item.partNumber}-${index}`)}
                    key={`${item.partNumber}-${index}`}
                    className={splitShipment ? bem('NoBorder') : ''}
                    locale={locale.country}
                >
                    {item.showOrderItemStatus && (
                        <p className={bem('status')}>
                            {deliveryTrackingEnabled
                                ? messages.orderItemStatus?.[item.activeOrderItemStatus]?.()
                                : item.orderItemStatus}
                        </p>
                    )}
                    {item.finalSale && !enableFinalSaleOrderItemsSplit && (
                        <Notification
                            className={bem('finalSaleMessage', 'alignInfo')}
                            message={messages.orderDetails.finalSale()}
                            type="info"
                        />
                    )}
                </LineItem>
                {showOverlay === `${item.partNumber}-${index}` ? (
                    <Overlay
                        onClose={toggleOverlay}
                        type="pdp"
                        className={{
                            content: bem('overlay'),
                        }}
                    >
                        <div className={bem('litePdpWithForm')}>
                            <LiteLayout
                                product={{
                                    partNumber: item.partNumber,
                                    designerName: item.brand,
                                    name: item.name,
                                }}
                                selectedProductVariant={{
                                    price: { finalPrice: item.price.finalPrice },
                                    images: item.images,
                                }}
                                messages={messages}
                                computeAttributes={isLegacyOrder === 'true' ? false : true}
                                locale={locale.id}
                            >
                                {item.consideredBadge && (
                                    <SingleBadge
                                        className={bem('consideredBadge')}
                                        considered={true}
                                        badge={{
                                            label: item.consideredBadge,
                                            key: 'CONSIDERED_KEY',
                                        }}
                                        translation={messages.considered.badge()}
                                    />
                                )}
                                <div className={bem('details')}>
                                    <p className={bem('detailsTitle')}>{`${messages.colourAndSize.colour()}: `}</p>
                                    <p className={bem('detailsInfo')}>{item.colour}</p>
                                </div>
                                <div className={bem('details')}>
                                    <p className={bem('detailsTitle')}>{`${messages.sizes.sizeLabel()}: `}</p>
                                    <p className={bem('detailsInfo')}>{item.size}</p>
                                </div>
                                <div className={bem('details')}>
                                    <p className={bem('detailsTitle')}>{`${messages.orderDetails.productCode()}: `}</p>
                                    <p className={bem('detailsInfo')}>{item.partNumber.replace(/(.{4})/g, '$1 ')}</p>
                                </div>
                                {isLegacyOrder != 'true' && (
                                    <Button
                                        type="link"
                                        component="a"
                                        href={item.productURL}
                                        target="_blank"
                                        rel="noopener"
                                        className={bem('productLink')}
                                    >
                                        {messages.orderDetails.viewMoreDetails()}
                                    </Button>
                                )}
                                {item.finalSale && (
                                    <Notification
                                        className={bem('finalSaleMessage')}
                                        message={messages.orderDetails.finalSale()}
                                        type="info"
                                    />
                                )}
                            </LiteLayout>
                        </div>
                    </Overlay>
                ) : null}
            </Fragment>
        ) : (
            <LineItemSkeleton key={`skeleton-${index}`} />
        )
    }

    return (
        <>
            <div className={bem('info')}>
                {(!fullPageHeader || guestUser) && (
                    <h2 className={bem('title', guestUser && 'guestUser')}>{messages.orderDetails.title()}</h2>
                )}
                <div className={bem('orderIdMessage')}>
                    {messages.orderDetails.orderInfo({
                        userTitle,
                        userLastName,
                    })}
                    <p className={bem('orderId')}>{externalOrderId.toUpperCase()}</p>
                </div>
                {showDelayMessaging && (
                    <Notification className={bem('orderDelay')} component={DelayComponent} type="info" />
                )}
                <div className={bem('message')}>
                    {orderDate || dateShipped ? (
                        dateShipped ? (
                            parse(
                                messages.orderDetails.dateShipped({
                                    displayId: displayId.toUpperCase(),
                                    dateShipped,
                                })
                            )
                        ) : (
                            userTitle &&
                            userLastName && (
                                <div>
                                    {messages.orderDetails.orderInfo({
                                        userTitle,
                                        userLastName,
                                    })}
                                    <p className={bem('orderId')}>{externalOrderId.toUpperCase()}</p>
                                </div>
                            )
                        )
                    ) : (
                        <Skeleton className={bem('messageSkeleton')} />
                    )}
                </div>
                {showOrderDetailsStatus && !splitShipment && <OrderDetailsStatus {...props} />}
                {!deliveryTrackingEnabled && trackingUrl && !returnTrackingUrl && (
                    <Button
                        type="secondary"
                        component="a"
                        href={trackingUrl}
                        target="_blank"
                        className={bem('trackOrder')}
                    >
                        {messages.orderDetails.trackOrder()}
                    </Button>
                )}
            </div>
            {showReturnsAndExchanges && (
                <ReturnsAndExchanges
                    messages={messages}
                    locale={locale}
                    urlPath={urlPath}
                    guestUser={guestUser}
                    exchanges={exchanges}
                    returns={returns}
                    returnTrackingUrl={returnTrackingUrl}
                />
            )}

            <div className={bem('returnExchange')}>
                <h3 className={bem('itemsTitle')}>{messages.orderDetails.orderItemsTitle({ orderItemTotal })}</h3>

                {!displayReturnFeeMessage && timeDelivered && isReturnable === 'true' && (
                    <div className={bem('timeDelivered')}>
                        {messages.orderDetails.timeDelivered({
                            timeDelivered,
                            returnWindow,
                        })}
                    </div>
                )}

                {!displayReturnFeeMessage && isReturnable === 'true' && (
                    <div className={bem('timeShipped')}>
                        {shortDateShipped &&
                            messages.orderDetails.shortDateShipped &&
                            parse(messages.orderDetails.shortDateShipped({ shortDateShipped }))}
                        {messages.orderDetails.returnsConditions?.({
                            returnWindow,
                        })}
                    </div>
                )}

                {splitShipment && (
                    <p className={bem('splitMessage')}>{messages.orderDetails?.splitShipmentMessage()}</p>
                )}

                <ReturnMessage
                    messages={messages}
                    locale={locale}
                    externalOrderId={externalOrderId}
                    isLegacyOrder={isLegacyOrder}
                    isReturnable={isReturnable}
                    returnsAllowedInCountry={returnsAllowedInCountry}
                    isExchangable={isExchangable}
                    urlPath={urlPath}
                    shippingMethod={shippingMethod}
                    returnWindow={returnWindow}
                    displayReturnFeeMessage={displayReturnFeeMessage}
                    localizedTimeDelivered={timeDelivered}
                    timeDelivered={shipmentsWithItemDetails[0]?.timeDelivered}
                    dateShipped={dateShipped}
                    displayId={displayId}
                    returnFeeInfo={returnFeeInfo}
                />
            </div>

            {shipmentsWithItemDetails?.map((shipment, index) => {
                const processing = shipment?.processing
                const delivered = shipment?.dateDelivered
                const shipmentShippingMethod = shipment?.shippingMethodName

                const splittedFinalSaleItems =
                    enableFinalSaleOrderItemsSplit && splitWithFinalSaleItems(shipment?.shipmentItemDetails)

                return (
                    <div className={bem('shipment', splitShipment ? 'splitShipment' : '')} key={index}>
                        {splitShipment && (shipment?.dateShipped || processing) && (
                            <>
                                <div className={bem('timeDelivered', 'splitShipment')}>
                                    {processing
                                        ? messages.orderTracking.processing()
                                        : delivered
                                        ? messages.orderTracking.delivered({
                                              timeDelivered: shipment?.dateDelivered,
                                          })
                                        : messages.orderTracking.dispatched({
                                              timeShipped: shipment?.dateShipped,
                                          })}
                                </div>
                                {shipmentShippingMethod && (
                                    <SingleBadge
                                        className={bem('shippingMethod')}
                                        badge={{
                                            label: messages.orderDetails.shippingMethodName({
                                                method: shipmentShippingMethod,
                                            }),
                                        }}
                                    />
                                )}
                            </>
                        )}
                        {enableFinalSaleOrderItemsSplit ? (
                            <>
                                {!!splittedFinalSaleItems?.regularItems?.length && (
                                    <ul className={bem('items', splitShipment ? 'splitShipment' : '')}>
                                        {splittedFinalSaleItems?.regularItems?.map((item, index) =>
                                            renderItem(item, index)
                                        )}
                                    </ul>
                                )}
                                {!!splittedFinalSaleItems?.finalSaleItems?.length && (
                                    <div
                                        className={bem(
                                            'finalSale',
                                            !splittedFinalSaleItems?.regularItems?.length && !splitShipment
                                                ? 'onlyFinalSaleItems'
                                                : ''
                                        )}
                                    >
                                        <div className={bem('finalSaleTitle')}>
                                            <h4>{messages.orderDetails.finalSaleItems.title()}</h4>
                                            <p>{messages.orderDetails.finalSaleItems.description()}</p>
                                        </div>
                                        <ul className={bem('items')}>
                                            {splittedFinalSaleItems?.finalSaleItems?.map((item, index) =>
                                                renderItem(item, index)
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </>
                        ) : (
                            <ul className={bem('items', splitShipment ? 'splitShipment' : '')}>
                                {shipment?.shipmentItemDetails?.map((item, index) => renderItem(item, index))}
                            </ul>
                        )}

                        {splitShipment && (
                            <Button
                                type="primary"
                                component={Link}
                                to={
                                    processing
                                        ? ''
                                        : `/${
                                              locale.id
                                          }/account/${urlPath}/${externalOrderId}/track?trackingUrl=${encodeURIComponent(
                                              shipment?.trackingUrl
                                          )}&courierTrackingId=${
                                              shipment?.courierTrackingId
                                          }&returnTrackingUrl=${encodeURIComponent(shipment?.returnTrackingUrl)}`
                                }
                                className={bem('trackButton', processing ? 'processing' : '')}
                            >
                                {shipment?.isReturnOrExchangeShipment
                                    ? messages.returnTracking.trackDetails()
                                    : messages.orderTracking.trackDetails()}
                            </Button>
                        )}
                    </div>
                )
            })}
            <h3 className={bem('totalsTitle')}>{messages.orderDetails.totalsTitle()}</h3>
            {totals ? (
                <Totals className={bem('totals')} {...totals} messages={messages} />
            ) : (
                <Skeleton className={bem('totalsSkeleton')} count={3} align="left" />
            )}
            <OrderPaymentSummary
                className={bem('paymentSummary')}
                messages={messages}
                shippingAddress={shippingAddress}
                shippingMethod={shippingMethod}
                paymentMethod={paymentMethod}
            />
        </>
    )
}

OrderInformation.propTypes = propTypes
export default OrderInformation
